import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { availableGroupIcons } from '../../../../../utils/available-icons';
import { PopUpNavLink } from '../../../../../components/popup-nav';
import {
  IconChannelsOrder,
  IconEdit,
  IconGridView,
  IconGroups,
  IconListView,
  IconOrder,
  IconAdd,
} from '../../../../../assets/popup-nav-icons';
import { useInstallationContext } from '../../../../../context/installation';
import { useRefetchData } from '../../../../../hooks/refresh-data/use-refetch-data';
import { ROUTES } from '../../../../../routes/routes';
import { diffHours } from '../../../../../utils/helpers';
import { getIcon } from '../../../../../utils/helpers/icons';
import * as exalusStorage from '../../../storage';
import { useExalusContext } from '../../../context';
import { useExalusServicesContext } from '../../../context/services';
import { useExalusStorageContext } from '../../../context/storage';
import { useLoggedUser } from '../../../hooks/common/logged-user';

export const useGroups = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('dashboard');
  const { t: tg } = useTranslation('groups');
  const { activeGroup, allGroups } = useExalusContext();
  const { installationList } = useInstallationContext();
  const { isDashboardGridView, toggleGridView } = useExalusStorageContext();
  const { groupsApi } = useExalusServicesContext();
  const { canAddEditGroup } = useLoggedUser();
  const { refetchAll } = useRefetchData();

  useEffect(() => {
    if (localStorage.getItem('local-app')) {
      const remindCreateAccount = exalusStorage.getItem('remindCreateAccount');
      if (!remindCreateAccount) navigate(ROUTES.CreateAccountOffer());
      else if (diffHours(new Date(remindCreateAccount), 72)) navigate(ROUTES.CreateAccountOffer());
    }
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!installationList.length) refetchAll();
    }, 300);

    return () => clearTimeout(timeout);
  }, [installationList.length]);

  const popUpNavLinks = useMemo((): PopUpNavLink[] => {
    return [
      ...(canAddEditGroup
        ? [
            {
              onClick: () => navigate(ROUTES.GroupEdit(activeGroup?.Guid)),
              label: t('groupEdit', { groupName: activeGroup?.Name }),
              hasLineBelow: true,
              icon: <IconEdit />,
            },
            { onClick: () => navigate(ROUTES.GroupCreate()), label: t('newGroup'), icon: <IconAdd /> },
            {
              onClick: () => navigate(ROUTES.GroupList()),
              label: tg('allGroups'),
              hasLineBelow: true,
              icon: <IconGroups />,
            },
          ]
        : []),
      ...(groupsApi.IsGroupsAndObjectsOrderingAvailable() && canAddEditGroup
        ? [
            {
              onClick: () => navigate(ROUTES.GroupSortChannels(!isDashboardGridView)),
              label: t('channelsOrder'),
              icon: <IconChannelsOrder />,
            },
            {
              onClick: () => navigate(ROUTES.GroupSort()),
              label: t('groupsOrder'),
              hasLineBelow: true,
              icon: <IconOrder />,
            },
          ]
        : []),
      {
        onClick: toggleGridView,
        label: !isDashboardGridView ? t('gridView') : t('listView'),
        icon: !isDashboardGridView ? <IconGridView /> : <IconListView />,
      },
    ];
  }, [activeGroup, isDashboardGridView, canAddEditGroup, t, tg]);

  const tabs = useMemo(() => {
    const items = allGroups.map((group) => {
      const Icon = getIcon(availableGroupIcons, group.IconName);
      return { label: group.Name, ...(Icon ? { icon: Icon.component } : {}) };
    });

    return [...items, { label: '+', onClick: () => navigate(ROUTES.GroupCreate()), addNew: true }];
  }, [allGroups]);

  return {
    popUpNavLinks,
    tabs,
  };
};
